import { Environment } from 'utils/environment';
import { HistoryHandler } from 'utils/history.handler';
import { Json } from 'types/common';
import { LocalStorageHandler } from 'utils/Local.storage.handler';
import { stringify } from 'qs';
import { ApiResult, ApiFailure } from 'types/api';
import axios, { AxiosResponse, AxiosRequestConfig } from 'axios';
import { LoginResponse, ApiData } from 'types/api/responses';
import { jwtDecode } from 'jwt-decode';
import { logout } from 'redux/actions/session.actions';
import { resetStore } from 'redux/actions/types.actions';

const karthusApi = axios.create({
  baseURL: Environment.baseUrl + '/v1',
  validateStatus: (code: number) => code !== 401,
  paramsSerializer: (params) =>
    stringify(params, {
      skipNulls: true,
      allowDots: true,
    }),
});

let isRefreshing = false;
const failedQueue: {
  resolve: (value: unknown) => void;
  reject: (reason: unknown) => void;
}[] = [];

export const isExpiredToken = (): boolean => {
  const token = LocalStorageHandler.getToken();
  if (!token) return false;
  const decoded: { exp: number } = jwtDecode(token);
  const now = Date.now() / 1000;
  return decoded.exp < now;
};

const retry = async (request: AxiosRequestConfig) => {
  try {
    const token = await new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });
    request.headers = request.headers || {};
    request.headers.Authorization = `Bearer ${token}`;
    return await karthusApi(request);
  } catch (err) {
    return await Promise.reject(err);
  }
};

const processQueue = (error: unknown, token: string) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue.length = 0;
};

karthusApi.interceptors.request.use((config) => {
  const token = LocalStorageHandler.getToken();
  config.headers.Authorization = `Bearer ${token}`;

  return config;
});

karthusApi.interceptors.response.use(
  (config) => config,
  async (error) => {
    const req = error.config;

    // Skip token refresh for login and refresh token endpoints
    const isAuthEndpoint = req.url?.includes('/login');
    if (isAuthEndpoint) {
      return Promise.reject(error);
    }

    if (error?.response?.status === 401 && !req.retry) {
      if (isRefreshing) {
        return retry(req);
      }
      const refresh = {
        refreshToken: LocalStorageHandler.getRefreshToken(),
      };
      req.retry = true;
      isRefreshing = true;
      try {
        return await new Promise((resolve, reject) => {
          axios
            .post<ApiData<LoginResponse>>(`${Environment.baseUrl}/v1/auth/refresh`, refresh, {
              headers: {
                Authorization: `Bearer ${LocalStorageHandler.getToken()}`,
              },
            })
            .then((res) => {
              const { accessToken, refreshToken } = res.data.data;
              LocalStorageHandler.setToken(accessToken);
              LocalStorageHandler.setRefreshToken(refreshToken);
              req.headers.Authorization = `Bearer ${accessToken}`;
              resolve(karthusApi(req));
              processQueue(null, accessToken);
            })
            .catch((err) => {
              LocalStorageHandler.clear();
              HistoryHandler.goTo('/');
              reject(err);
            });
        });
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  },
);

const mapResponse = <T extends Json>(res: AxiosResponse<T>): T | Error[] => {
  // this check is because no matter what there should be at least 1 error if something fails
  if (res.data && res.data.hasOwnProperty('errors')) {
    const error = (res.data as unknown) as ApiFailure;
    if (error.errors) {
      return error.errors.map((e) => ({
        name: e.code.toString(),
        message: e?.message,
      }));
    }
  }

  if (res?.status >= 200 && res?.status < 300) {
    return res.data;
  }

  switch (res?.status) {
    case 500:
      return [{ name: 'Server error', message: 'Server error' }];
    case 401:
      return [{ name: 'Not authorize', message: 'Not authorize' }];
    case 404:
      return [{ name: 'Resource not found', message: 'Resource not found' }];
    default:
      return [{ name: 'Default error', message: 'Ups... something went wrong' }];
  }
};

const unwrapResponse = <T extends Json>(response: AxiosResponse<T>): ApiResult<T> => {
  return {
    status: response?.status,
    statusText: response?.statusText || 'Unknown error',
    result: mapResponse(response),
  };
};

const createApiFailure = <T>(e: unknown): ApiResult<T> => {
  const error = e instanceof Error ? e : new Error('Unknown error');

  if (isExpiredToken()) {
    resetStore();
    logout();
    HistoryHandler.goTo('/login');
  }
  return {
    status: -1,
    statusText: 'REACT APP ERROR',
    result: [error],
  };
};

export const apiGet = async <T extends Json>(
  endpoint: string,
  params?: Json,
): Promise<ApiResult<T>> => {
  try {
    const res = await karthusApi.get<T>(endpoint, { params });
    return unwrapResponse(res);
  } catch (e) {
    return createApiFailure(e);
  }
};

export const apiPut = async <T extends Json>(
  endpoint: string,
  data: Json,
): Promise<ApiResult<T>> => {
  try {
    const res = await karthusApi.put<T>(endpoint, data);
    return unwrapResponse(res);
  } catch (e) {
    return createApiFailure(e);
  }
};

export const apiPost = async <T extends Json>(
  endpoint: string,
  data: Json,
): Promise<ApiResult<T>> => {
  try {
    const res = await karthusApi.post<T>(endpoint, data);
    return unwrapResponse(res);
  } catch (e: any) {

    if (e.response) {
      if (e.response.status === 401) {
        let errorMessage = 'Credenciales inválidas';
        let errorData = e.response.data;
        
        if (typeof errorData === 'string') {
          try {
            errorData = JSON.parse(errorData);
          } catch (parseError) {
            console.error('Error parsing error response:', parseError);
          }
        }
        
        if (errorData?.errors?.[0]?.message) {
          errorMessage = errorData.errors[0].message;
        } else if (errorData?.message) {
          errorMessage = errorData.message;
        }
        
        return {
          status: e.response.status,
          statusText: e.response.statusText || 'Unauthorized',
          result: [{
            name: 'AuthenticationError',
            message: errorMessage,
          }],
        };
      }
      
      return {
        status: e.response.status,
        statusText: e.response.statusText || 'Error',
        result: [{
          name: `HTTP_${e.response.status}`,
          message: e.response.data?.message || e.response.statusText || 'Error desconocido',
        }],
      };
    }
    
    return createApiFailure(e);
  }
};

export const apiPatch = async <T extends Json>(
  endpoint: string,
  data: Json,
): Promise<ApiResult<T>> => {
  try {
    const res = await karthusApi.patch<T>(endpoint, data);
    return unwrapResponse(res);
  } catch (e) {
    return createApiFailure(e);
  }
};

export const apiDelete = async <T extends Json>(
  endpoint: string,
  data: Json,
): Promise<ApiResult<T>> => {
  try {
    const res = await karthusApi.delete<T>(endpoint, { data });
    return unwrapResponse(res);
  } catch (e) {
    return createApiFailure(e);
  }
};

export const getBlobTransfer = async (
  endPoint: string,
  params = {},
): Promise<ApiResult<Blob>> => {
  const headers: AxiosRequestConfig = {
    responseType: 'blob',
    params,
  };
  try {
    const response = await karthusApi.get<Blob>(endPoint, headers);
    return {
      status: response.status,
      statusText: response.statusText,
      result: response.data,
    };
  } catch (e) {
    return createApiFailure(e);
  }
};

// Comisionist endpoints
export const getCommissionerList = async (
  params: {
    sort?: string;
    filter?: {
      nameContains?: string;
      account?: string;
      type?: string;
      primaryStatus?: string;
      secondaryStatus?: string;
      OfficeId?: string;
      updatedAfter?: string;
    };
    size?: number;
    page?: number;
  } = {}
): Promise<ApiResult<any>> => {
  return apiGet('/commissioner', params);
};

export const getCommissionerStats = async (
  filter: {
    nameContains?: string;
    account?: string;
    type?: string;
    primaryStatus?: string;
    secondaryStatus?: string;
    OfficeId?: string;
    ZoneId?: number;
    updatedAfter?: string;
  } = {}
): Promise<ApiResult<any>> => {
  return apiGet('/commissioner/stats', {filter: filter});
};

export const getCommissionerDetail = async (clientAccount: string): Promise<ApiResult<any>> => {
  return apiGet(`/commissioner/${clientAccount}`);
};

export const updateCommissioner = async (clientAccount: string, data: any): Promise<ApiResult<any>> => {
  return apiPut(`/commissioner/${clientAccount}`, data);
};

export const uploadCommissionerDocument = async (clientAccount: string, docType: string, file: File): Promise<ApiResult<any>> => {
  const formData = new FormData();
  formData.append('file', file);
  
  // Custom call to handle multipart/form-data
  try {
    const res = await axios.post(`${Environment.baseUrl}/v1/commissioner/document/${clientAccount}/${docType}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${LocalStorageHandler.getToken()}`,
      },
    });
    return {
      status: res.status,
      statusText: res.statusText,
      result: res.data,
    };
  } catch (e: any) {
    return {
      status: e.response?.status || 500,
      statusText: e.response?.statusText || 'Error',
      result: [{ name: 'UploadError', message: e.response?.data?.message || 'Error al subir el documento' }],
    };
  }
};

export const deleteCommissionerDocument = async (clientAccount: string, docType: string): Promise<ApiResult<any>> => {
  return apiDelete(`/commissioner/document/${clientAccount}/${docType}`, {});
};

export const getCommissionerHistory = async (clientAccount: string): Promise<ApiResult<any>> => {
  return apiGet(`/commissioner/history/${clientAccount}`);
};

// System Parameters endpoints
export const getSystemParameters = async (): Promise<ApiResult<any>> => {
  return apiGet('/system-parameter');
};

export const updateSystemParameter = async (key: string, data: any): Promise<ApiResult<any>> => {
  return apiPost(`/system-parameter`, data);
};
